/**
 * Firebase Auth utilities for anonymous user flow with cart persistence
 * Handles anonymous login, cart management, address handling, and auth state transitions
 */

import firebase from 'firebase';
import { auth, firestore } from './firebase';

/**
 * Sign in user anonymously
 * @returns {Promise<{success: boolean, user?: any, error?: string}>}
 */
export const signInAnonymously = async (pickupOrDelivery = 'pickup') => {
  const maxRetries = 3;
  let retryCount = 0;

  while (retryCount < maxRetries) {
    try {
      if (!auth) {
        throw new Error('Firebase auth not initialized');
      }

      const userCredential = await auth.signInAnonymously();
      const user = userCredential.user;

      // Validate the user object
      if (!user || !user.uid) {
        throw new Error('Invalid user object returned from Firebase');
      }

      if (!user.isAnonymous) {
        throw new Error('User is not anonymous as expected');
      }

      return { success: true, user, pickupOrDelivery };
    } catch (error) {
      retryCount++;
      console.error(
        `❌ [AUTH] Anonymous sign-in failed (attempt ${retryCount}/${maxRetries}):`,
        error
      );

      if (retryCount >= maxRetries) {
        return { success: false, error: error.message };
      }

      // Wait before retrying (exponential backoff)
      await new Promise((resolve) =>
        setTimeout(resolve, Math.pow(2, retryCount) * 1000)
      );
    }
  }
};

/**
 * Create or get anonymous user document
 * @param {string} userId - Anonymous user ID
 * @returns {Promise<{success: boolean, userData?: any, error?: string}>}
 */
export const createAnonymousUserDoc = async (
  userId,
  pickupOrDelivery = 'pickup'
) => {
  const maxRetries = 3;
  let retryCount = 0;

  while (retryCount < maxRetries) {
    try {
      if (!userId) {
        throw new Error('User ID is required');
      }

      const userDocRef = firestore.collection('users').doc(userId);
      const userDoc = await userDocRef.get();

      if (userDoc.exists) {
        const userData = userDoc.data();

        // Ensure the document has the required fields
        if (!userData.uid) {
          userData.uid = userId;
        }

        return { success: true, userData };
      }

      // Create new anonymous user document
      const anonymousUserData = {
        uid: userId,
        isAnonymous: true,
        bagItems: [],
        addresses: [],
        favorites: [],
        fullName: '',
        email: '',
        phoneNumber: '',
        defaultStore: '',
        pickupOrDelivery: pickupOrDelivery,
        defaultLanguage: 'en',
        // Use server timestamps so times are consistent and set by Firestore
        createdAt: firebase.firestore.FieldValue.serverTimestamp(),
        lastActiveAt: firebase.firestore.FieldValue.serverTimestamp(),
        // Anonymous-specific fields
        anonymousSession: {
          startedAt: firebase.firestore.FieldValue.serverTimestamp(),
          cartStartedAt: null,
          hasAddedItems: false,
          hasSetAddress: false,
        },
      };

      await userDocRef.set(anonymousUserData);

      // Verify the document was created by reading it back
      const verificationDoc = await userDocRef.get();
      if (!verificationDoc.exists) {
        throw new Error('Failed to verify user document creation');
      }

      const createdData = verificationDoc.data();

      return { success: true, userData: { ...createdData, uid: userId } };
    } catch (error) {
      retryCount++;
      console.error(
        `❌ [AUTH] Failed to create anonymous user document (attempt ${retryCount}/${maxRetries}):`,
        error
      );

      if (retryCount >= maxRetries) {
        return { success: false, error: error.message };
      }

      // Wait before retrying (exponential backoff)
      await new Promise((resolve) =>
        setTimeout(resolve, Math.pow(2, retryCount) * 1000)
      );
    }
  }
};

/**
 * Add item to anonymous user's cart
 * @param {string} userId - Anonymous user ID
 * @param {Array} cartItems - Updated cart items
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const updateAnonymousUserCart = async (userId, cartItems) => {
  try {
    const updateData = {
      bagItems: cartItems,
      // update lastActive to server timestamp
      lastActiveAt: firebase.firestore.FieldValue.serverTimestamp(),
      'anonymousSession.hasAddedItems': cartItems.length > 0,
    };

    // Only set cartStartedAt if this is the first item being added
    if (cartItems.length === 1) {
      updateData['anonymousSession.cartStartedAt'] =
        firebase.firestore.FieldValue.serverTimestamp();
    }

    await firestore.collection('users').doc(userId).update(updateData);

    return { success: true };
  } catch (error) {
    console.error('❌ [AUTH] Failed to update anonymous user cart:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Add address to anonymous user
 * @param {string} userId - Anonymous user ID
 * @param {Object} address - Address object
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const addAnonymousUserAddress = async (userId, address) => {
  try {
    const addressWithId = {
      ...address,
      id: `addr_${Date.now()}`,
      // Let Firestore populate a server timestamp for createdAt
      createdAt: firebase.firestore.FieldValue.serverTimestamp(),
      isAnonymousAddress: true,
    };

    await firestore
      .collection('users')
      .doc(userId)
      .update({
        addresses: firebase.firestore.FieldValue.arrayUnion(addressWithId),
        lastActiveAt: firebase.firestore.FieldValue.serverTimestamp(),
        'anonymousSession.hasSetAddress': true,
      });

    return { success: true };
  } catch (error) {
    console.error('❌ [AUTH] Failed to add address to anonymous user:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Link anonymous account with authenticated account
 * @param {Object} credential - Authentication credential (email/phone)
 * @returns {Promise<{success: boolean, user?: any, error?: string}>}
 */
export const linkAnonymousAccount = async (credential) => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser || !currentUser.isAnonymous) {
      throw new Error('No anonymous user to link');
    }

    const userCredential = await currentUser.linkWithCredential(credential);
    const user = userCredential.user;

    return { success: true, user };
  } catch (error) {
    console.error('❌ [AUTH] Failed to link anonymous account:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Migrate anonymous user data to authenticated account
 * @param {string} anonymousUserId - Anonymous user ID
 * @param {string} authenticatedUserId - Authenticated user ID
 * @param {boolean} isNewUser - Whether the authenticated user is new
 * @returns {Promise<{success: boolean, migratedData?: any, error?: string}>}
 */
export const migrateAnonymousUserData = async (
  anonymousUserId,
  authenticatedUserId,
  isNewUser = false
) => {
  try {
    // Get anonymous user data
    const anonymousDoc = await firestore
      .collection('users')
      .doc(anonymousUserId)
      .get();
    if (!anonymousDoc.exists) {
      return { success: true, migratedData: null };
    }

    const anonymousData = anonymousDoc.data();

    // Get anonymous user addresses from subcollection
    const anonymousAddressesRef = firestore
      .collection('users')
      .doc(anonymousUserId)
      .collection('addresses');
    const anonymousAddressesSnapshot = await anonymousAddressesRef.get();
    const anonymousAddresses = [];

    anonymousAddressesSnapshot.forEach((doc) => {
      anonymousAddresses.push({ id: doc.id, ...doc.data() });
    });

    // Get existing authenticated user data
    const authenticatedDoc = await firestore
      .collection('users')
      .doc(authenticatedUserId)
      .get();
    const existingData = authenticatedDoc.exists ? authenticatedDoc.data() : {};

    // Get existing authenticated user addresses from subcollection
    const authenticatedAddressesRef = firestore
      .collection('users')
      .doc(authenticatedUserId)
      .collection('addresses');
    const authenticatedAddressesSnapshot =
      await authenticatedAddressesRef.get();
    const existingAddresses = [];

    authenticatedAddressesSnapshot.forEach((doc) => {
      existingAddresses.push({ id: doc.id, ...doc.data() });
    });

    // Prepare migration data for main user document
    const migrationData = {
      // Cart migration logic: Always migrate anonymous cart if it has items
      // If anonymous user has items in cart, migrate them (regardless of hasAddedItems flag)
      bagItems: (() => {
        const anonymousCartItems = anonymousData.bagItems || [];
        const existingCartItems = existingData.bagItems || [];
        // Primary check: if anonymous cart has items, migrate them
        // The hasAddedItems flag is secondary - if items exist, they were added
        const hasAnonymousItems = anonymousCartItems.length > 0;

        // Always migrate anonymous cart if it has items
        if (hasAnonymousItems) {
          return anonymousCartItems; // Replace existing cart with anonymous cart
        } else {
          return existingCartItems; // Keep existing cart if no anonymous items
        }
      })(),

      // Always migrate favorites (merge and deduplicate)
      favorites: [
        ...(existingData.favorites || []),
        ...(anonymousData.favorites || []),
      ].filter((item, index, arr) => arr.indexOf(item) === index), // Remove duplicates

      // Update metadata
      lastActiveAt: firebase.firestore.FieldValue.serverTimestamp(),
      migratedFromAnonymous: {
        originalUserId: anonymousUserId,
        migratedAt: firebase.firestore.FieldValue.serverTimestamp(),
        migratedData: {
          cartItems: anonymousData.bagItems?.length || 0,
          cartMigrated: (() => {
            const anonymousCartItems = anonymousData.bagItems || [];
            return anonymousCartItems.length > 0;
          })(),
          cartMigrationReason: (() => {
            const anonymousCartItems = anonymousData.bagItems || [];
            const hasAnonymousItems = anonymousCartItems.length > 0;
            const hasAddedItemsFlag =
              anonymousData.anonymousSession?.hasAddedItems === true;
            if (hasAnonymousItems) {
              return hasAddedItemsFlag
                ? 'anonymous cart items migrated - items added while anonymous (hasAddedItems flag was set)'
                : 'anonymous cart items migrated - items exist in cart (hasAddedItems flag was not set)';
            } else if (isNewUser) {
              return 'new user - no anonymous items to migrate';
            } else {
              return 'existing user - no anonymous items to migrate';
            }
          })(),
          addresses: 0, // Will be set after address migration
          favorites: anonymousData.favorites?.length || 0,
        },
      },
    };

    // Handle address migration
    let migratedAddressCount = 0;
    const shouldMigrateAddresses = isNewUser || existingAddresses.length === 0;

    if (shouldMigrateAddresses && anonymousAddresses.length > 0) {
      // Migrate each address to the authenticated user's addresses subcollection
      for (const address of anonymousAddresses) {
        const addressData = {
          ...address,
          isAnonymousAddress: false, // Convert to regular address
          migratedAt: firebase.firestore.FieldValue.serverTimestamp(),
          migratedFrom: anonymousUserId,
        };

        // Remove the original document ID to let Firestore generate a new one
        delete addressData.id;

        // Add to authenticated user's addresses subcollection
        await authenticatedAddressesRef.add(addressData);
        migratedAddressCount++;
      }

      // If this is the first address for the user, set it as default
      if (existingAddresses.length === 0 && anonymousAddresses.length > 0) {
        const firstAddress = anonymousAddresses[0];
        migrationData.defaultDeliveryAddress = firstAddress.id; // This will be updated to the new ID
      }
    }

    // Update migration metadata with actual migrated address count
    migrationData.migratedFromAnonymous.migratedData.addresses =
      migratedAddressCount;

    // Update authenticated user document
    await firestore
      .collection('users')
      .doc(authenticatedUserId)
      .update(migrationData);

    // Clean up anonymous user data
    // Delete addresses subcollection first
    for (const address of anonymousAddresses) {
      await anonymousAddressesRef.doc(address.id).delete();
    }

    // Delete main anonymous user document
    await firestore.collection('users').doc(anonymousUserId).delete();

    return {
      success: true,
      migratedData: {
        ...migrationData,
        migratedAddressCount,
      },
    };
  } catch (error) {
    console.error('❌ [AUTH] Anonymous user data migration failed:', {
      error: error.message,
      stack: error.stack,
      anonymousUserId,
      authenticatedUserId,
      isNewUser,
    });
    return { success: false, error: error.message };
  }
};

/**
 * Get current user's anonymous status
 * @returns {Object} Anonymous status info
 */
export const getUserAnonymousStatus = () => {
  const currentUser = auth.currentUser;

  return {
    isLoggedIn: !!currentUser,
    isAnonymous: currentUser?.isAnonymous || false,
    uid: currentUser?.uid || null,
    needsAuthentication: currentUser?.isAnonymous || false,
  };
};

/**
 * Handle anonymous user authentication requirement at checkout
 * @param {Function} onAuthRequired - Callback when authentication is required
 * @returns {boolean} Whether user can proceed to checkout
 */
export const handleCheckoutAuthRequirement = (onAuthRequired) => {
  const status = getUserAnonymousStatus();

  if (!status.isLoggedIn) {
    onAuthRequired('login');
    return false;
  }

  if (status.isAnonymous) {
    onAuthRequired('upgrade');
    return false;
  }

  return true;
};

/**
 * Clean up expired anonymous users (utility function)
 * @param {number} maxAgeHours - Maximum age in hours before cleanup
 * @returns {Promise<{success: boolean, cleanedCount?: number, error?: string}>}
 */
export const cleanupExpiredAnonymousUsers = async (maxAgeHours = 72) => {
  try {
    const cutoffDate = new Date(Date.now() - maxAgeHours * 60 * 60 * 1000);

    const expiredUsersQuery = await firestore
      .collection('users')
      .where('isAnonymous', '==', true)
      .where('lastActiveAt', '<', cutoffDate)
      .limit(100) // Process in batches
      .get();

    const batch = firestore.batch();
    let cleanedCount = 0;

    expiredUsersQuery.docs.forEach((doc) => {
      batch.delete(doc.ref);
      cleanedCount++;
    });

    if (cleanedCount > 0) {
      await batch.commit();
    } else {
      console.log('ℹ️ [AUTH] No expired anonymous users to clean up');
    }

    return { success: true, cleanedCount };
  } catch (error) {
    console.error(
      '❌ [AUTH] Failed to clean up expired anonymous users:',
      error
    );
    return { success: false, error: error.message };
  }
};
